import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import {
  GetShipmentCountsForFiltersQuery,
  GetShipmentsQuery,
  useGetShipmentCountsForFiltersLazyQuery,
} from "@api/graphql/generated/generated-types";
import {
  currentEpochSeconds,
  minutesAfterCurrentEpochSeconds,
} from "@src/common/lib/DateUtils";
import { useAuth } from "@src/common/hooks/useAuth";
import { datePickerDateToISODate } from "@src/common/utils/timezoneConverters";
import { useShipmentFiltersContext } from "../context/ShipmentFiltersContext";
import { makeShipmentFilterVariables } from "../utils/makeShipmentFilterVariables";
import { useShipmentFilterSnapshot } from "./useShipmentFilterSnapshot";

export interface ShipmentsOverviewCounts {
  numLate: number | undefined;
  numLateIn15Minutes: number | undefined;
  numLateIn30Minutes: number | undefined;
  numLateIn60Minutes: number | undefined;
  numLikelyLate: number | undefined;
  numUnassigned: number | undefined;
  numAssigned: number | undefined;
  numActive: number | undefined;
  numDelivered: number | undefined;
  numClosed: number | undefined;
  numOnHold: number | undefined;
  customFilterOverviews: { name: string; count: number }[] | undefined;
}

export function mapResponseToCounts(
  response: GetShipmentCountsForFiltersQuery
): ShipmentsOverviewCounts {
  const overview = response.shipmentsOverview;
  return {
    numLate: overview.numLate || undefined,
    numLateIn15Minutes: overview.numLateIn15Minutes || undefined,
    numLateIn30Minutes: overview.numLateIn30Minutes || undefined,
    numLateIn60Minutes: overview.numLateIn60Minutes || undefined,
    numLikelyLate: overview.numLikelyLate || undefined,
    numUnassigned: overview.numUnassigned || undefined,
    numAssigned: overview.numAssigned || undefined,
    numActive: overview.numActive || undefined,
    numDelivered: overview.numDelivered || undefined,
    numClosed: overview.numClosed || undefined,
    numOnHold: overview.numOnHold || undefined,
    customFilterOverviews: overview.customFilterOverviews || undefined,
  };
}

export function useShipmentCounts(
  combinedShipmentsResponse: GetShipmentsQuery | undefined
): ShipmentsOverviewCounts | null {
  const { courierId } = useAuth();
  const {
    minDate,
    maxDate,
    selectedCustomer,
    selectedDriverIdFilter,
    shipmentDateRangeStart,
    shipmentDateRangeEnd,
    completedDateRangeStart,
    completedDateRangeEnd,
  } = useShipmentFiltersContext();
  const router = useRouter();
  const filterSnapshot = useShipmentFilterSnapshot();
  const [counts, setCounts] = useState<ShipmentsOverviewCounts | null>(null);

  // No pollInterval: the list query polls every 10s, and the chain effect on
  // combinedShipmentsResponse fires this query off each list response. Adding a
  // poll here would double-fire counts and risk variable drift between the queries.
  const [getShipmentCounts, { data: shipmentCountsResponse }] =
    useGetShipmentCountsForFiltersLazyQuery({ fetchPolicy: "no-cache" });

  const fetchShipmentCounts = () => {
    if (!courierId) return;
    const isSalesReport = router.pathname.includes("/office/sales");

    // minExpectedCompletionDate / maxShipmentDate / endCustomerIdsFilter / driverIdsFilter
    // are constructed inline here (rather than via makeShipmentFilterVariables) because
    // makeGetShipmentsVariable produces them for the list query. That helper bakes in
    // primary/custom-filter overrides which the count query intentionally must NOT
    // inherit — primary and custom filters are bucket selectors, not constraints.
    getShipmentCounts({
      variables: {
        courierId,
        now: currentEpochSeconds(),
        in15Minutes: minutesAfterCurrentEpochSeconds(15),
        in30Minutes: minutesAfterCurrentEpochSeconds(30),
        in60Minutes: minutesAfterCurrentEpochSeconds(60),
        minExpectedCompletionDate: minDate
          ? datePickerDateToISODate(minDate)
          : undefined,
        minShipmentDate: shipmentDateRangeStart
          ? datePickerDateToISODate(shipmentDateRangeStart)
          : undefined,
        maxShipmentDate: (shipmentDateRangeEnd ?? maxDate)
          ? datePickerDateToISODate((shipmentDateRangeEnd ?? maxDate)!)
          : undefined,
        minCompletedDate: completedDateRangeStart
          ? datePickerDateToISODate(completedDateRangeStart)
          : undefined,
        maxCompletedDate: completedDateRangeEnd
          ? datePickerDateToISODate(completedDateRangeEnd)
          : undefined,
        endCustomerIdsFilter: selectedCustomer
          ? [selectedCustomer.id]
          : undefined,
        driverIdsFilter: selectedDriverIdFilter
          ? [selectedDriverIdFilter]
          : undefined,
        ...makeShipmentFilterVariables(filterSnapshot, { isSalesReport }),
      },
    });
  };

  useEffect(() => {
    if (combinedShipmentsResponse) {
      fetchShipmentCounts();
    }
  }, [combinedShipmentsResponse]);

  useEffect(() => {
    if (shipmentCountsResponse) {
      setCounts(mapResponseToCounts(shipmentCountsResponse));
    }
  }, [shipmentCountsResponse]);

  return counts;
}
